import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, query, setDoc } from 'firebase/firestore';
import { db } from '../../../../shared/storage/firebase';
import type { OpsCheckpointDefinition, OpsTemplate, OpsTemplateCheckpoint, OpsTemplateCheckpointRef, OpsTemplateTask } from '../../types';
import { ACTIVE_OPS_LOGISTICS_CATALOG as OPS_LOGISTICS_CATALOG } from '../../catalog/activeLogisticsCatalog';
import SearchableSelect from '../shared/SearchableSelect';
import LogisticsMultiPickModal from '../shared/LogisticsMultiPickModal';
import { slugify } from '../../../../shared/utils/slugify';
import { assertValidTemplateIds } from '../../utils/validate';

export default function TemplatesTab(): React.ReactElement {
  const [templates, setTemplates] = useState<OpsTemplate[]>([]);
  const [checkpointDefs, setCheckpointDefs] = useState<OpsCheckpointDefinition[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragCheckpointIdx, setDragCheckpointIdx] = useState<number | null>(null);
  const [dragOverCheckpointIdx, setDragOverCheckpointIdx] = useState<number | null>(null);
  const [dragRefIdx, setDragRefIdx] = useState<number | null>(null);
  const [dragOverRefIdx, setDragOverRefIdx] = useState<number | null>(null);
  const [dragTaskKey, setDragTaskKey] = useState<null | { cpIdx: number; tIdx: number }>(null);
  const [dragOverTaskKey, setDragOverTaskKey] = useState<null | { cpIdx: number; tIdx: number }>(null);
  const [collapsedCheckpointIds, setCollapsedCheckpointIds] = useState<Record<string, boolean>>({});
  const [editingCheckpointId, setEditingCheckpointId] = useState<string | null>(null);
  const [editingTemplateTitle, setEditingTemplateTitle] = useState(false);
  const [editingTaskKeyTitle, setEditingTaskKeyTitle] = useState<string | null>(null);
  const [addTasksModalFor, setAddTasksModalFor] = useState<null | { cpIdx: number; checkpointId: string }>(null);
  const [addTasksSelectedLogisticIds, setAddTasksSelectedLogisticIds] = useState<string[]>([]);
  const [addCheckpointOpen, setAddCheckpointOpen] = useState(false);
  const [addCheckpointPickId, setAddCheckpointPickId] = useState<string>('');

  const logisticOptions = useMemo(
    () => OPS_LOGISTICS_CATALOG.map(l => ({ id: l.id, label: l.label })),
    []
  );

  const checkpointOptions = useMemo(
    () => checkpointDefs.map(c => ({ id: c.checkpointId, label: `${c.title || c.checkpointId} — ${c.checkpointId}` })),
    [checkpointDefs]
  );

  const checkpointById = useMemo(() => {
    const m = new Map<string, OpsCheckpointDefinition>();
    checkpointDefs.forEach(c => m.set(String(c.checkpointId), c));
    return m;
  }, [checkpointDefs]);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'opsTemplates')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), templateId: d.id } as OpsTemplate));
      list.sort((a, b) => String(a.title || a.templateId).localeCompare(String(b.title || b.templateId)));
      setTemplates(list);
      if (!selectedId && list[0]?.templateId) setSelectedId(list[0].templateId);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'opsCheckpoints')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), checkpointId: d.id } as OpsCheckpointDefinition));
      list.sort((a, b) => String(a.title || a.checkpointId).localeCompare(String(b.title || b.checkpointId)));
      setCheckpointDefs(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setError(null);
    if (!selectedId) {
      setDraft(null);
      return;
    }

    const t = templates.find(x => x.templateId === selectedId) || null;

    // If the selected template exists in Firestore, always sync draft from it.
    if (t) {
      setDraft(JSON.parse(JSON.stringify(t)));
      return;
    }

    // Otherwise, keep an in-progress local draft (e.g. just clicked "New") if it matches the selectedId.
    setDraft((prev: any) => {
      const prevId = String(prev?.templateId || '').trim();
      if (prev && prevId === selectedId) return prev;
      return null;
    });
  }, [selectedId, templates]);

  const nowMs = () => Date.now();

  const normalizeTemplate = (t: any) => {
    const cps = Array.isArray(t?.checkpoints) ? t.checkpoints : [];
    const nextCps = cps.map((cp: any, cpIdx: number) => {
      const tasks = Array.isArray(cp?.tasks) ? cp.tasks : [];
      const nextTasks = tasks.map((task: any, tIdx: number) => ({ ...task, order: tIdx, required: task?.required ?? true }));
      return { ...cp, order: cpIdx, tasks: nextTasks };
    });
    return { ...t, checkpoints: nextCps };
  };

  const newCheckpoint = (namespace: string, title: string, order: number): OpsTemplateCheckpoint => {
    const slug = slugify(title || 'checkpoint');
    const checkpointId = `${namespace}.${slug || 'checkpoint'}`.replace(/\.{2,}/g, '.');
    return { checkpointId, title: title || 'New checkpoint', order, tasks: [] };
  };

  const newTask = (checkpointId: string, title: string, order: number): OpsTemplateTask => {
    const slug = slugify(title || 'task');
    const taskId = `${checkpointId}.${slug || 'task'}`.replace(/\.{2,}/g, '.');
    return { taskId, title: title || 'New task', order, required: true };
  };

  const sanitizeIdForTask = (id: string) => String(id || '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const createNew = () => {
    setError(null);
    const t = nowMs();
    const templateId = `template.${t}`;
    setSelectedId(templateId);
    setDraft({
      templateId,
      title: 'New template',
      description: '',
      checkpointRefs: [] as OpsTemplateCheckpointRef[],
      createdAt: t,
      createdBy: '',
      updatedAt: t,
      updatedBy: '',
    } as OpsTemplate);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const t = nowMs();
      const payload: any = { ...(draft as any), updatedAt: t };
      const templateId = String(payload.templateId || '').trim();
      if (!templateId) throw new Error('templateId is required.');
      assertValidTemplateIds(payload);
      const ref = doc(db, 'opsTemplates', templateId);
      const { templateId: _ignore, ...rest } = payload;
      await setDoc(ref, rest, { merge: true });
    } catch (e: any) {
      setError(String(e?.message || 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const t = nowMs();
      const templateId = `template.${t}`;
      const payload: any = {
        ...(JSON.parse(JSON.stringify(draft)) as any),
        templateId,
        title: `${String((draft as any).title || 'Template').trim() || 'Template'} (copy)`,
        createdAt: t,
        updatedAt: t,
        createdBy: '',
        updatedBy: '',
      };
      assertValidTemplateIds(payload);
      const ref = doc(db, 'opsTemplates', templateId);
      const { templateId: _ignore, ...rest } = payload;
      await setDoc(ref, rest, { merge: true });
      setSelectedId(templateId);
    } catch (e: any) {
      setError(String(e?.message || 'Failed to duplicate.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await window.appConfirm('Delete this item? This cannot be undone.');
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const templateId = String((draft as any).templateId || '').trim();
      if (!templateId) return;
      await deleteDoc(doc(db, 'opsTemplates', templateId));
      setSelectedId(null);
    } catch (e: any) {
      setError(String(e?.message || 'Failed to delete.'));
    } finally {
      setSaving(false);
    }
  };

  const list = templates;
  const idKey = 'templateId';
  const titleKey = 'title';

  const namespace = useMemo(() => {
    if (!draft) return 'template';
    const id = String((draft as any)[idKey] || '').trim();
    // Use first segment as namespace by default (e.g. "stripe" from "stripe.marketingReady")
    return (id.split('.')[0] || 'template').trim() || 'template';
  }, [draft, idKey]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 4 }}>Templates</h2>
          <div className="muted">Create templates made of checkpoints + logistics tasks. Flows can apply templates later (append + merge by IDs).</div>
        </div>
      </div>

      {error ? <div className="billing-error" style={{ marginTop: 10 }}>{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 14, marginTop: 14 }}>
        {/* Left list */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800 }}>Library</div>
            <button className="mini-btn" type="button" onClick={createNew}>New</button>
          </div>

          <div style={{ maxHeight: 520, overflow: 'auto' }}>
            {list.length === 0 ? (
              <div className="muted">None yet.</div>
            ) : (
              list.map((x: any) => {
                const id = String(x[idKey] || '').trim();
                const title = String(x[titleKey] || id || '').trim();
                const isActive = id && id === selectedId;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedId(id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 10,
                      marginBottom: 8,
                      background: isActive ? '#f3f4f6' : 'white',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827' }}>{title || id}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{id}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right editor */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          {!draft ? (
            <div className="muted">Select or create an item.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {editingTemplateTitle ? (
                    <input
                      value={String((draft as any).title || '')}
                      autoFocus
                      onChange={(e) => setDraft((d: any) => ({ ...d, title: e.target.value }))}
                      onBlur={() => setEditingTemplateTitle(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditingTemplateTitle(false);
                      }}
                      style={{ fontWeight: 900, fontSize: 18 }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingTemplateTitle(true)}
                      title="Click to rename template"
                      style={{
                        fontWeight: 900,
                        fontSize: 18,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'text',
                        color: '#111827',
                        textAlign: 'left',
                      }}
                    >
                      {String((draft as any).title || 'Template')}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="mini-btn" type="button" onClick={() => void duplicate()} disabled={saving}>
                    Duplicate
                  </button>
                  <button className="mini-btn danger" type="button" onClick={remove} disabled={saving}>
                    Delete
                  </button>
                  <button className="btn btn-primary" type="button" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800 }}>Checkpoints</div>
                {Array.isArray((draft as any).checkpointRefs) ? (
                  <button className="mini-btn" type="button" onClick={() => setAddCheckpointOpen(v => !v)}>
                    + Add checkpoint
                  </button>
                ) : (
                  <button className="mini-btn" type="button" onClick={() => setAddCheckpointOpen(v => !v)}>
                    + Add checkpoint
                  </button>
                )}
              </div>

              {addCheckpointOpen ? (
                <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Add from library</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 10, alignItems: 'center' }}>
                    <SearchableSelect
                      valueId={addCheckpointPickId}
                      placeholder="Search checkpoints…"
                      options={checkpointOptions}
                      disabled={saving}
                      onSelectId={(id) => setAddCheckpointPickId(id)}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!String(addCheckpointPickId || '').trim()}
                      onClick={() => {
                        const checkpointId = String(addCheckpointPickId || '').trim();
                        if (!checkpointId) return;
                        setDraft((d: any) => {
                          // New model: store checkpointRefs
                          if (Array.isArray(d.checkpointRefs)) {
                            const refs = [...d.checkpointRefs];
                            if (refs.some((r: any) => String(r.checkpointId) === checkpointId)) return d;
                            refs.push({ checkpointId, order: refs.length } as OpsTemplateCheckpointRef);
                            return { ...d, checkpointRefs: refs };
                          }

                          // Legacy model: clone checkpoint definition into inline checkpoints
                          const def = checkpointById.get(checkpointId);
                          if (!def) return d;
                          const cps = Array.isArray(d.checkpoints) ? [...d.checkpoints] : [];
                          if (cps.some((cp: any) => String(cp.checkpointId) === checkpointId)) return d;
                          cps.push({ ...(def as any), order: cps.length });
                          return normalizeTemplate({ ...d, checkpoints: cps });
                        });
                        setAddCheckpointPickId('');
                        setAddCheckpointOpen(false);
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Manage checkpoints in the Checkpoints tab. Templates reference them.
                  </div>
                </div>
              ) : null}

              <div style={{ marginTop: 10 }}>
                {Array.isArray((draft as any).checkpointRefs) ? (
                  ((draft as any).checkpointRefs as OpsTemplateCheckpointRef[]).length === 0 ? (
                    <div className="muted">No checkpoints yet.</div>
                  ) : (
                    ((draft as any).checkpointRefs as OpsTemplateCheckpointRef[])
                      .slice()
                      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
                      .map((ref, idx) => {
                        const cpId = String(ref.checkpointId || '').trim();
                        const def = checkpointById.get(cpId);
                        const title = String(def?.title || cpId || 'Checkpoint');
                        const isCollapsed = Boolean(collapsedCheckpointIds[cpId]);
                        return (
                          <div
                            key={cpId || idx}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: 12,
                              padding: 12,
                              marginBottom: 10,
                              outline: dragOverRefIdx === idx ? '2px solid #60a5fa' : 'none',
                              outlineOffset: 2,
                            }}
                            onDragOver={(e) => {
                              if (dragRefIdx == null) return;
                              e.preventDefault();
                              setDragOverRefIdx(idx);
                            }}
                            onDrop={(e) => {
                              if (dragRefIdx == null) return;
                              e.preventDefault();
                              const from = dragRefIdx;
                              const to = idx;
                              setDragRefIdx(null);
                              setDragOverRefIdx(null);
                              if (from === to) return;
                              setDraft((d: any) => {
                                const refs = [...(d.checkpointRefs || [])].sort((a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0));
                                const [moved] = refs.splice(from, 1);
                                refs.splice(to, 0, moved);
                                const normalized = refs.map((r: any, i: number) => ({ ...r, order: i }));
                                return { ...d, checkpointRefs: normalized };
                              });
                            }}
                            onDragLeave={() => {
                              if (dragOverRefIdx === idx) setDragOverRefIdx(null);
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button
                                  type="button"
                                  className="mini-btn"
                                  draggable
                                  onDragStart={(e) => {
                                    setDragRefIdx(idx);
                                    setDragOverRefIdx(null);
                                    try {
                                      e.dataTransfer.setData('text/plain', `checkpointRef:${idx}`);
                                      e.dataTransfer.effectAllowed = 'move';
                                    } catch {
                                      // ignore
                                    }
                                  }}
                                  onDragEnd={() => {
                                    setDragRefIdx(null);
                                    setDragOverRefIdx(null);
                                  }}
                                  title="Drag to reorder"
                                  style={{ cursor: 'grab' }}
                                >
                                  ⋮
                                </button>
                                <button
                                  type="button"
                                  className="mini-btn"
                                  onClick={() => setCollapsedCheckpointIds(prev => ({ ...prev, [cpId]: !prev[cpId] }))}
                                  title={isCollapsed ? 'Expand' : 'Collapse'}
                                  style={{ width: 34, textAlign: 'center' }}
                                >
                                  {isCollapsed ? '▸' : '▾'}
                                </button>
                                <div style={{ fontWeight: 900, fontSize: 16, lineHeight: '32px' }}>{title}</div>
                              </div>
                              <button
                                type="button"
                                className="mini-btn danger"
                                onClick={() => setDraft((d: any) => {
                                  const refs = Array.isArray(d.checkpointRefs) ? [...d.checkpointRefs] : [];
                                  const next = refs.filter((r: any) => String(r.checkpointId) !== cpId);
                                  const normalized = next.map((r: any, i: number) => ({ ...r, order: i }));
                                  return { ...d, checkpointRefs: normalized };
                                })}
                              >
                                Remove
                              </button>
                            </div>
                            {!isCollapsed ? (
                              <div style={{ marginTop: 10 }}>
                                {(def?.tasks || []).length === 0 ? (
                                  <div className="muted">No tasks.</div>
                                ) : (
                                  (def?.tasks || [])
                                    .slice()
                                    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
                                    .map((t) => (
                                      <div key={t.taskId} style={{ padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
                                        <div>{t.title}</div>
                                        <div className="muted" style={{ fontSize: 12 }}>
                                          {t.logisticId ? t.logisticId : ''}
                                        </div>
                                      </div>
                                    ))
                                )}
                              </div>
                            ) : (
                              <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                                {Number((def?.tasks || []).length)} tasks (collapsed)
                              </div>
                            )}
                          </div>
                        );
                      })
                  )
                ) : (
                  (draft.checkpoints as OpsTemplateCheckpoint[]).map((cp, cpIdx) => (
                    (() => {
                      const cpId = String(cp.checkpointId || '').trim();
                      const isCollapsed = Boolean(collapsedCheckpointIds[cpId]);
                      const isEditing = Boolean(cpId && editingCheckpointId === cpId);
                      return (
                    <div
                      key={cp.checkpointId || cpIdx}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: 12,
                        marginBottom: 10,
                        outline: dragOverCheckpointIdx === cpIdx ? '2px solid #60a5fa' : 'none',
                        outlineOffset: 2,
                      }}
                      onDragOver={(e) => {
                        if (dragCheckpointIdx == null) return;
                        e.preventDefault();
                        setDragOverCheckpointIdx(cpIdx);
                      }}
                      onDrop={(e) => {
                        if (dragCheckpointIdx == null) return;
                        e.preventDefault();
                        const from = dragCheckpointIdx;
                        const to = cpIdx;
                        setDragCheckpointIdx(null);
                        setDragOverCheckpointIdx(null);
                        if (from === to) return;
                        setDraft((d: any) => {
                          const cps = [...(d.checkpoints || [])];
                          const [moved] = cps.splice(from, 1);
                          cps.splice(to, 0, moved);
                          return normalizeTemplate({ ...d, checkpoints: cps });
                        });
                      }}
                      onDragLeave={() => {
                        if (dragOverCheckpointIdx === cpIdx) setDragOverCheckpointIdx(null);
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button
                            type="button"
                            className="mini-btn"
                            onClick={() => {
                              if (!cpId) return;
                              setCollapsedCheckpointIds(prev => ({ ...prev, [cpId]: !prev[cpId] }));
                            }}
                            title={isCollapsed ? 'Expand tasks' : 'Collapse tasks'}
                            style={{ width: 34, textAlign: 'center' }}
                          >
                            {isCollapsed ? '▸' : '▾'}
                          </button>
                          <button
                            type="button"
                            className="mini-btn"
                            draggable
                            onDragStart={(e) => {
                              setDragCheckpointIdx(cpIdx);
                              setDragOverCheckpointIdx(null);
                              try {
                                e.dataTransfer.setData('text/plain', `checkpoint:${cpIdx}`);
                                e.dataTransfer.effectAllowed = 'move';
                              } catch {
                                // ignore
                              }
                            }}
                            onDragEnd={() => {
                              setDragCheckpointIdx(null);
                              setDragOverCheckpointIdx(null);
                            }}
                            title="Drag to reorder checkpoints"
                            style={{ cursor: 'grab' }}
                          >
                            ⋮⋮
                          </button>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isEditing ? (
                              <input
                                value={String(cp.title || '')}
                                autoFocus
                                onChange={(e) => setDraft((d: any) => {
                                  const cps = [...(d.checkpoints || [])];
                                  cps[cpIdx] = { ...cps[cpIdx], title: e.target.value };
                                  return normalizeTemplate({ ...d, checkpoints: cps });
                                })}
                                onBlur={() => setEditingCheckpointId(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  if (e.key === 'Escape') setEditingCheckpointId(null);
                                }}
                                style={{ fontWeight: 900, fontSize: 16, lineHeight: '32px' }}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => cpId && setEditingCheckpointId(cpId)}
                                title="Click to rename checkpoint"
                                style={{
                                  fontWeight: 900,
                                  fontSize: 16,
                                  lineHeight: '32px',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'text',
                                  color: '#111827',
                                  textAlign: 'left',
                                }}
                              >
                                {String(cp.title || 'Checkpoint')}
                              </button>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="mini-btn danger"
                          onClick={() => setDraft((d: any) => {
                            const cps = [...(d.checkpoints || [])];
                            cps.splice(cpIdx, 1);
                            return normalizeTemplate({ ...d, checkpoints: cps });
                          })}
                        >
                          Remove
                        </button>
                      </div>

                      {!isCollapsed ? (
                        <>
                          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontWeight: 800 }}>Tasks</div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <button
                                className="mini-btn"
                                type="button"
                                onClick={() => {
                                  setAddTasksSelectedLogisticIds([]);
                                  setAddTasksModalFor({ cpIdx, checkpointId: String(cp.checkpointId || '').trim() });
                                }}
                              >
                                + Add logistics
                              </button>
                              <button
                                className="mini-btn"
                                type="button"
                                onClick={() => setDraft((d: any) => {
                                  const cps = [...(d.checkpoints || [])];
                                  const cur = cps[cpIdx];
                                  const tasks = Array.isArray(cur.tasks) ? cur.tasks : [];
                                  const order = tasks.length;
                                  const nextTask = newTask(cur.checkpointId, `Task ${order + 1}`, order);
                                  cps[cpIdx] = { ...cur, tasks: [...tasks, nextTask] };
                                  return normalizeTemplate({ ...d, checkpoints: cps });
                                })}
                                title="Add a process-only task (no logistic)"
                              >
                                + Process task
                              </button>
                            </div>
                          </div>

                          <LogisticsMultiPickModal
                            open={Boolean(addTasksModalFor && addTasksModalFor.cpIdx === cpIdx)}
                            title="Add logistics as tasks"
                            logistics={OPS_LOGISTICS_CATALOG}
                            selectedIds={addTasksSelectedLogisticIds}
                            onChangeSelectedIds={setAddTasksSelectedLogisticIds}
                            onClose={() => setAddTasksModalFor(null)}
                            onConfirm={() => {
                              const ctx = addTasksModalFor;
                              if (!ctx) return;
                              const logisticIds = addTasksSelectedLogisticIds;
                              setDraft((d: any) => {
                                const cps = [...(d.checkpoints || [])];
                                const cur = { ...cps[ctx.cpIdx] };
                                const tasks = Array.isArray(cur.tasks) ? [...cur.tasks] : [];
                                const existingLogisticIds = new Set(tasks.map((t: any) => String(t.logisticId || '').trim()).filter(Boolean));
                                const existingTaskIds = new Set(tasks.map((t: any) => String(t.taskId || '').trim()).filter(Boolean));

                                for (const logisticIdRaw of logisticIds) {
                                  const logisticId = String(logisticIdRaw || '').trim();
                                  if (!logisticId) continue;
                                  if (existingLogisticIds.has(logisticId)) continue;
                                  const def = OPS_LOGISTICS_CATALOG.find(x => String(x.id) === logisticId);
                                  const title = String(def?.label || logisticId);
                                  const taskId = `${cur.checkpointId}.li.${sanitizeIdForTask(logisticId)}`.replace(/\.{2,}/g, '.');
                                  if (existingTaskIds.has(taskId)) continue;
                                  tasks.push({ taskId, title, order: tasks.length, required: true, logisticId });
                                  existingLogisticIds.add(logisticId);
                                  existingTaskIds.add(taskId);
                                }

                                cur.tasks = tasks;
                                cps[ctx.cpIdx] = cur;
                                return normalizeTemplate({ ...d, checkpoints: cps });
                              });
                              setAddTasksModalFor(null);
                            }}
                          />

                          <div style={{ marginTop: 10 }}>
                            {(cp.tasks || []).length === 0 ? <div className="muted">No tasks yet.</div> : null}
                          </div>
                          {/* Task editor list rendered below without hooks */}
                          {(cp.tasks || []).map((t: OpsTemplateTask, tIdx: number) => {
                            const logisticId = String(t.logisticId || '').trim();
                            const isTaskDragOver =
                              dragOverTaskKey?.cpIdx === cpIdx && dragOverTaskKey?.tIdx === tIdx && dragTaskKey?.cpIdx === cpIdx;
                            const editKey = `${cpIdx}:${tIdx}`;
                            const isEditingTaskTitle = editingTaskKeyTitle === editKey;
                            return (
                              <div
                                key={t.taskId || tIdx}
                                style={{
                                  borderTop: '1px solid #f3f4f6',
                                  paddingTop: 10,
                                  marginTop: 10,
                                  outline: isTaskDragOver ? '2px solid #60a5fa' : 'none',
                                  outlineOffset: 2,
                                  borderRadius: 8,
                                }}
                                onDragOver={(e) => {
                                  if (!dragTaskKey) return;
                                  if (dragTaskKey.cpIdx !== cpIdx) return;
                                  e.preventDefault();
                                  setDragOverTaskKey({ cpIdx, tIdx });
                                }}
                                onDrop={(e) => {
                                  if (!dragTaskKey) return;
                                  if (dragTaskKey.cpIdx !== cpIdx) return;
                                  e.preventDefault();
                                  const from = dragTaskKey.tIdx;
                                  const to = tIdx;
                                  setDragTaskKey(null);
                                  setDragOverTaskKey(null);
                                  if (from === to) return;
                                  setDraft((d: any) => {
                                    const cps = [...(d.checkpoints || [])];
                                    const cur = { ...cps[cpIdx] };
                                    const tasks = [...(cur.tasks || [])];
                                    const [moved] = tasks.splice(from, 1);
                                    tasks.splice(to, 0, moved);
                                    cur.tasks = tasks;
                                    cps[cpIdx] = cur;
                                    return normalizeTemplate({ ...d, checkpoints: cps });
                                  });
                                }}
                                onDragLeave={() => {
                                  if (dragOverTaskKey?.cpIdx === cpIdx && dragOverTaskKey?.tIdx === tIdx) setDragOverTaskKey(null);
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
                                    <button
                                      type="button"
                                      className="mini-btn"
                                      draggable
                                      onDragStart={(e) => {
                                        setDragTaskKey({ cpIdx, tIdx });
                                        setDragOverTaskKey(null);
                                        try {
                                          e.dataTransfer.setData('text/plain', `task:${cpIdx}:${tIdx}`);
                                          e.dataTransfer.effectAllowed = 'move';
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                      onDragEnd={() => {
                                        setDragTaskKey(null);
                                        setDragOverTaskKey(null);
                                      }}
                                      title="Drag to reorder tasks"
                                      style={{ cursor: 'grab' }}
                                    >
                                      ⋮⋮
                                    </button>

                                    {isEditingTaskTitle ? (
                                      <input
                                        value={String(t.title || '')}
                                        autoFocus
                                        onChange={(e) => setDraft((d: any) => {
                                          const cps = [...(d.checkpoints || [])];
                                          const cur = { ...cps[cpIdx] };
                                          const tasks = [...(cur.tasks || [])];
                                          tasks[tIdx] = { ...tasks[tIdx], title: e.target.value };
                                          cur.tasks = tasks;
                                          cps[cpIdx] = cur;
                                          return normalizeTemplate({ ...d, checkpoints: cps });
                                        })}
                                        onBlur={() => setEditingTaskKeyTitle(null)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                          if (e.key === 'Escape') setEditingTaskKeyTitle(null);
                                        }}
                                        style={{ fontWeight: 800, fontSize: 16, lineHeight: '32px' }}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setEditingTaskKeyTitle(editKey)}
                                        title="Click to rename task"
                                        style={{
                                          fontWeight: 800,
                                          fontSize: 16,
                                          lineHeight: '32px',
                                          background: 'transparent',
                                          border: 'none',
                                          padding: '6px 0',
                                          cursor: 'text',
                                          color: '#111827',
                                          textAlign: 'left',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          maxWidth: 320,
                                        }}
                                      >
                                        {String(t.title || 'Task')}
                                      </button>
                                    )}

                                    <div style={{ width: 360, maxWidth: '45vw' }}>
                                      <SearchableSelect
                                        valueId={logisticId}
                                        placeholder="Pick logistic (optional)…"
                                        options={logisticOptions}
                                        disabled={saving}
                                        onSelectId={(id) => setDraft((d: any) => {
                                          const cps = [...(d.checkpoints || [])];
                                          const cur = { ...cps[cpIdx] };
                                          const tasks = [...(cur.tasks || [])];
                                          tasks[tIdx] = { ...tasks[tIdx], logisticId: id, title: tasks[tIdx].title || (OPS_LOGISTICS_CATALOG.find(x => x.id === id)?.label || id) };
                                          cur.tasks = tasks;
                                          cps[cpIdx] = cur;
                                          return normalizeTemplate({ ...d, checkpoints: cps });
                                        })}
                                      />
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="mini-btn danger"
                                    onClick={() => setDraft((d: any) => {
                                      const cps = [...(d.checkpoints || [])];
                                      const cur = { ...cps[cpIdx] };
                                      const tasks = [...(cur.tasks || [])];
                                      tasks.splice(tIdx, 1);
                                      cur.tasks = tasks;
                                      cps[cpIdx] = cur;
                                      return normalizeTemplate({ ...d, checkpoints: cps });
                                    })}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                          {Number((cp.tasks || []).length)} tasks (collapsed)
                        </div>
                      )}
                    </div>
                      );
                    })()
                  ))
                )}
              </div>

              <div className="muted" style={{ marginTop: 12, fontSize: 12 }}>
                IDs are stable. Apply behavior merges by ID; duplicates are rejected on save.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

